// Presence status (状态): 在线 / 离开 / 忙碌 / 请勿打扰, an optional note (写方案中) and when it goes back to 在线 by itself. The
// office keeps it on my Raft login and enforces the expiry (src/office/status.ts); this is the chip in the bottom bar,
// the menu it opens (also in 设置 → 状态), and what everyone else's status looks like (the name tag, the online list,
// the person card read it through statusOf / STATES). Everything can be set with taps alone (the note has ready-made
// choices), so a phone never needs its keyboard for it.
// 请勿打扰 is decided by the office, not here: on the open deck, nearby voice, text chat and captions don't reach me and
// mine don't reach the people near me (both ways), greetings are refused (E_DND); inside a hut or 会议室 the room's
// rules hold, and broadcasts work as usual. 离开 and 忙碌 are labels only.
// 离开 also comes by itself: when the tab goes to the background, or after IDLE_MS with no touch, key, pointer or my own
// voice, the page says {s:"away", auto: true}; the next touch (or the tab coming back) clears it. The office keeps
// a status set by hand over an automatic one, and "back" only clears the automatic one.
import { icon, label } from '/icons.js';
import { t, tParts, has, onLang, fmtTime } from '/i18n.js';

export const NOTE_MAX = 40;
/** The four states (the wire sends the id, never the words): their text keys, pixel icons (public/icons: dot_good,
 *  moon, busy, dnd) and colours; .name is the text in the current language. "available" (在线) is the wire name of
 *  none set. */
const state = (text, icon, color) => ({ text, icon, color, get name() { return t(this.text); } });
export const STATES = {
  available: state('status.state.available', 'dot_good', '#22c55e'),
  away: state('status.state.away', 'moon', '#a16207'),
  busy: state('status.state.busy', 'busy', '#f59e0b'),
  dnd: state('status.state.dnd', 'dnd', '#ef4444'),
};
/** The ready-made notes: what goes on the wire (the words as they always were, so every page, old or new, shows them
 *  as before) -> the key it's shown with here. Any other note is the person's own words and shows as written. */
const NOTES = { '写方案中': 'status.note.drafting', '开会中': 'status.note.meeting', '专注中': 'status.note.focus', '午休': 'status.note.lunch', '外出': 'status.note.out' };
/** A note as this page shows it: a ready-made one in the reader's language, else as written. */
export const noteText = (n) => (n && NOTES[n] ? t(NOTES[n]) : n);
/** Typed text that is a ready-made note's words in the reader's language: that note's wire words. */
const noteOf = (text) => Object.keys(NOTES).find((n) => t(NOTES[n]) === text) ?? text;
const OFF_HOUR = 18; // 今天下班前: 18:00 in this device's time zone
/** No touch, key, pointer or voice for this long (a tab in the background included): 离开 by itself. */
const IDLE_MS = 5 * 60_000;
/** How near (tiles) someone on 请勿打扰 shows clearly on the deck though they aren't heard: their name tag stays opaque
 *  and their card shows (app.js badge, greet.js). The office's HEAR_RELEASE, where hearing would let go. */
export const DND_NEAR = 6.5;

/** p's status in force now: {s, note?, until?, auto?}, or null for 在线 (none, an agent, or past its expiry). */
export function statusOf(p, now = Date.now()) {
  const s = p?.status;
  return s && STATES[s.s] && s.s !== 'available' && (!s.until || s.until > now) ? s : null;
}
const hhmm = (d) => fmtTime(d, { hour: '2-digit', minute: '2-digit', hour12: false });
/** 「到 15:30」 (today) or 「到 10月31日 09:00」 */
export function untilText(at) {
  if (!at) return '';
  const d = new Date(at), today = new Date();
  return d.toDateString() === today.toDateString() ? t('status.until.today', { time: hhmm(at) }) : t('status.until.date', { month: d.getMonth() + 1, day: d.getDate(), time: hhmm(at) });
}
/** One line for a card or a tooltip: 「请勿打扰 · 写方案中 · 到 18:00」 */
export function statusLine(s) {
  if (!s) return STATES.available.name;
  return [s.auto ? t('status.line.auto', { state: STATES[s.s].name }) : STATES[s.s].name, noteText(s.note), untilText(s.until)].filter(Boolean).join(' · ');
}

/** 18:00 today on this device (ms since epoch) */
const offTime = (now = Date.now()) => { const d = new Date(now); d.setHours(OFF_HOUR, 0, 0, 0); return d.getTime(); };
/** The expiry choices: [id, words, what goes in the message ({for}: a duration the office adds to its own clock, so a
 *  wrong clock here doesn't matter; {until}: 18:00 here; {}: 不自动恢复), shown disabled (18:00 has passed)]. */
function expiries(now = Date.now()) {
  return [
    ['30m', t('status.exp.min30'), { for: 30 * 60_000 }],
    ['1h', t('status.exp.hour1'), { for: 60 * 60_000 }],
    ['off', t('status.exp.offWork', { hour: OFF_HOUR }), { until: offTime(now) }, offTime(now) <= now + 60_000],
    ['never', t('status.exp.never'), {}],
  ];
}

const CSS = `#statusChip .px-icon{margin-right:0}
#statusMenu{position:fixed;left:50%;bottom:calc(var(--barh, calc(68px + var(--sb, 0px))) + 8px);max-height:calc(100vh - var(--barh, 76px) - 24px - var(--st, 0px));max-height:calc(100dvh - var(--barh, 76px) - 24px - var(--st, 0px));overflow:auto;transform:translateX(-50%);display:none;z-index:6;width:min(360px,calc(100vw - 24px));box-sizing:border-box;background:#fff8e7;border:3px solid #1b1410;border-radius:16px;padding:10px 12px;font:14px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);color:#1b1410}
.stpanel{display:flex;flex-direction:column;gap:8px}
.stpanel .now{font-size:13px;color:#555}
.stpanel .seg{display:flex;flex-wrap:wrap;gap:6px}
.stpanel .seg button{font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:4px 10px;background:#fff;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.stpanel .seg button.sel{background:#FDD03B}
.stpanel .seg button:disabled{opacity:.45;cursor:default}
.stpanel .lab{font-size:12px;color:#666;margin-bottom:-4px}
.stpanel .hint{font-size:12px;background:#fde2e2;border:2px solid #ef4444;border-radius:10px;padding:5px 8px;display:flex;gap:6px;align-items:flex-start}
.stpanel .hint.away{background:#fef3c7;border-color:#d97706}
.stpanel input{font:inherit;padding:5px 8px;border:2px solid #1b1410;border-radius:10px;min-width:0;width:100%;box-sizing:border-box}
#statusMenu .stout{display:block;margin:12px auto 0 0;font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:4px 12px;background:#fff;color:#1b1410;cursor:pointer}
.stpanel .go{font:inherit;font-weight:700;border:2px solid #1b1410;border-radius:999px;padding:6px 14px;background:#FDD03B;cursor:pointer;align-self:flex-end}
.stdot{display:inline-block;width:8px;height:8px;border-radius:50%;border:1px solid #1b1410;margin-right:4px;vertical-align:0}`;

/** The panel: pick a state, a note and an expiry, then 设定. Built fresh each time it opens (the menu, 设置, or the
 *  pre-join page, public/prejoin.js, which queues the message until it's on board).
 *  @param box where it goes  @param p {cur: my status now (statusOf) or null, send(msg), toast(text, ...), done()} */
export function statusPanel(box, { cur, send, toast, done }) {
  css();
  let pick = cur && !cur.auto ? cur.s : 'available', note = cur?.note ?? '', exp = 'never';
  const root = document.createElement('div'); root.className = 'stpanel';
  const now = document.createElement('div'); now.className = 'now';
  now.append(...tParts('status.panel.now', { icon: icon(STATES[cur?.s ?? 'available'].icon) }, { line: statusLine(cur) }));
  const states = document.createElement('div'); states.className = 'seg';
  const hint = document.createElement('div'); hint.className = 'hint'; hint.append(icon('dnd'), document.createTextNode(t('status.hint.dnd')));
  const ahint = document.createElement('div'); ahint.className = 'hint away'; ahint.append(icon('moon'), document.createTextNode(t('status.hint.away')));
  const more = document.createElement('div'); more.className = 'stpanel';
  const noteLab = document.createElement('div'); noteLab.className = 'lab'; noteLab.textContent = t('status.panel.noteLabel', { max: NOTE_MAX });
  const inp = document.createElement('input'); inp.maxLength = NOTE_MAX; inp.placeholder = t('status.panel.notePlaceholder'); inp.value = noteText(note);
  inp.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Enter' && !e.isComposing) apply(); if (e.key === 'Escape') done(); });
  inp.addEventListener('keyup', (e) => e.stopPropagation());
  inp.oninput = () => { note = noteOf([...inp.value].slice(0, NOTE_MAX).join('')); drawNotes(); }; // a ready-made note's words: its wire words
  const notes = document.createElement('div'); notes.className = 'seg';
  const expLab = document.createElement('div'); expLab.className = 'lab'; expLab.textContent = t('status.panel.expLabel');
  const exps = document.createElement('div'); exps.className = 'seg';
  const go = document.createElement('button'); go.className = 'go'; go.textContent = t('status.panel.set');
  more.append(noteLab, inp, notes, expLab, exps);
  root.append(now, states, hint, ahint, more, go);
  box.replaceChildren(root);

  function seg(el, items, sel, onPick) {
    el.replaceChildren(...items.map(([id, text, ic, disabled]) => {
      const b = document.createElement('button'); b.type = 'button'; b.dataset.id = id;
      if (ic) label(b, ic, text); else b.textContent = text;
      b.classList.toggle('sel', id === sel); b.disabled = !!disabled;
      b.onclick = () => onPick(id);
      return b;
    }));
  }
  function drawStates() { seg(states, Object.entries(STATES).map(([id, s]) => [id, s.name, s.icon]), pick, (id) => { pick = id; draw(); }); }
  function drawNotes() { seg(notes, Object.keys(NOTES).map((n) => [n, noteText(n)]), note, (n) => { note = note === n ? '' : n; inp.value = noteText(note); drawNotes(); }); }
  function drawExps() { seg(exps, expiries().map(([id, text, , past]) => [id, text, null, past]), exp, (id) => { exp = id; drawExps(); }); }
  function draw() { drawStates(); drawNotes(); drawExps(); hint.style.display = pick === 'dnd' ? '' : 'none'; ahint.style.display = pick === 'away' ? '' : 'none'; more.style.display = pick === 'available' ? 'none' : ''; }
  draw();
  function apply() {
    const e = expiries().find(([id]) => id === exp) ?? ['never', '', {}];
    // 今天下班前 picked, and 18:00 went by while the menu was open: say so, rather than quietly never restoring
    if (pick !== 'available' && e[3]) { toast(t('status.toast.past', { hour: OFF_HOUR })); drawExps(); return; }
    const msg = pick === 'available' ? { t: 'status', s: 'available' } : { t: 'status', s: pick, ...(note.trim() ? { note: note.trim() } : {}), ...e[2] };
    send(msg);
    if (pick === 'dnd' && cur?.s !== 'dnd') toast(t('status.toast.dnd'), null, 'dnd');
    done();
  }
  go.onclick = apply;
}

let styled = false;
function css() { if (styled) return; styled = true; const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st); }

/**
 * @param o {send, me, toast, signOut ({label, run}: the menu's last button, 退出登录 / a guest's 离开 Deck), bar (the bottom bar element), addSettingsSection, changed (someone's status changed: redraw lists),
 *   active (walking or talking: not idle), present() (the 小窗 is open: a tab in the background isn't 离开), room (this floor's office room, public/floors.js), server() (my Raft server id)}
 * @returns {{msg(m), at(m), refresh()}} msg: the office's {t:"status"}; at: its {t:"status_at"}; refresh: welcome
 */
export function initStatus(o) {
  css();
  const panel = (box, done) => statusPanel(box, { cur: statusOf(o.me()), send: o.send, toast: o.toast, done });
  let chip = document.getElementById('statusChip');
  if (!chip) { chip = document.createElement('button'); chip.id = 'statusChip'; o.bar?.prepend(chip); }
  const menu = document.createElement('div'); menu.id = 'statusMenu'; document.body.append(menu);

  const shown = () => menu.style.display === 'block';
  // the menu ends with 退出登录 (a guest: 离开 Deck), as in the header of 设置 (public/profile.js, app.js signOut)
  const open = () => {
    panel(menu, close);
    if (o.signOut) {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'stout'; b.textContent = o.signOut.label;
      b.onclick = () => { close(); o.signOut.run(); };
      menu.append(b);
    }
    menu.style.display = 'block'; chip.classList.add('on');
  };
  const close = () => { menu.style.display = 'none'; chip.classList.remove('on'); };
  chip.onclick = () => (shown() ? close() : open());
  addEventListener('pointerdown', (e) => { if (shown() && !menu.contains(e.target) && !chip.contains(e.target)) close(); }, true);
  addEventListener('keydown', (e) => { if (e.key === 'Escape' && shown()) { e.preventDefault(); close(); } }); // handled: 钓鱼 mode stays open
  o.addSettingsSection?.({ id: 'status', title: () => t('status.title'), icon: 'dnd', order: 5, render(box) { panel(box, () => { setTimeout(() => panel(box, () => {}), 300); }); } });

  function refresh() {
    const s = statusOf(o.me()), d = STATES[s?.s ?? 'available'];
    label(chip, d.icon, d.name);
    chip.setAttribute('aria-label', t('status.chip.label', { state: d.name })); // on a phone the chip shows its icon alone (index.html)
    chip.title = s ? t('status.chip.tip', { line: statusLine(s) }) : t('status.chip.tipAvailable');
  }
  refresh();
  onLang(refresh); // the chip's words (an open menu is built fresh on the next open)
  setInterval(() => { refresh(); }, 15_000); // an expiry passing (the office says so too)

  // ---- 离开 by itself: the tab goes to the background (at once: a phone freezes a page in the background, so no timer
  // would run there), or IDLE_MS with no touch, key, pointer, walking or talking; back on the next touch / the tab
  // coming back. Changes are spaced AUTO_GAP apart; one that comes too soon is made when the gap is over, from the state
  // as it is then (never dropped, so nobody stays on 离开 after coming back).
  const idleMs = () => window.__deckIdleMs ?? IDLE_MS; // tests shorten it
  const AUTO_GAP = 1500;
  let lastActive = Date.now(), autoAt = 0, autoTimer = 0;
  // the tab in the background counts as away, unless the 小窗 (public/pip.js) is open: then I'm still here (until idle)
  const away = () => document.hidden && !o.present?.();
  function reconcile() {
    const me = o.me(); if (!me) return;
    if (!away() && o.active?.()) lastActive = Date.now(); // talking (my mic) or walking: not idle
    const idle = away() || Date.now() - lastActive >= idleMs(), cur = statusOf(me);
    const want = idle && !cur ? 'away' : !idle && cur?.auto ? 'available' : null; // never over a status set by hand (the office checks too)
    if (!want) return;
    const wait = autoAt + AUTO_GAP - Date.now();
    if (wait > 0) { clearTimeout(autoTimer); autoTimer = setTimeout(reconcile, wait + 50); return; }
    autoAt = Date.now(); o.send({ t: 'status', s: want, auto: true });
  }
  function poke() { lastActive = Date.now(); if (statusOf(o.me())?.auto) reconcile(); }
  for (const ev of ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart']) addEventListener(ev, poke, { capture: true, passive: true });
  document.addEventListener('visibilitychange', () => { if (away()) reconcile(); else poke(); });
  setInterval(reconcile, Math.min(15_000, Math.max(500, idleMs() / 4)));

  // ---- across floors: each floor is its own office (src/office/floors.ts), which keeps its own copy of my status. The
  // status I set by hand comes with me when this tab goes to another floor (sessionStorage: this tab, my login and Raft
  // server only), with when it was chosen ({t:"status_at"}, the office's clock); the new floor takes it only if nothing
  // was chosen there later (src/office/status.ts decides). 0: never chosen, nothing to bring.
  const CARRY = 'deck-status-carry';
  const handSet = (s) => (s && !s.auto ? { s: s.s, ...(s.note ? { note: s.note } : {}), ...(s.until ? { until: s.until } : {}) } : null);
  let chosenAt = null; // this floor's {t:"status_at"}; null until it comes (right after welcome)
  const where = () => ({ sub: o.me()?.sub, server: o.server?.() ?? null, room: o.room ?? 'main' });
  function remember() {
    const me = o.me(); if (!me?.sub || chosenAt == null) return;
    try { sessionStorage.setItem(CARRY, JSON.stringify({ ...where(), s: handSet(statusOf(me)), at: chosenAt })); } catch {}
  }
  /** {t:"status_at"}: when my status here was last chosen by hand. The first one after welcome: bring the one from the
   *  floor I came from, if it was chosen at all (the office keeps it only if it's newer than the choice here). */
  function arrived(at) {
    const first = chosenAt == null; chosenAt = at;
    const me = o.me(), w = where();
    let c = null; if (first) try { c = JSON.parse(sessionStorage.getItem(CARRY) ?? 'null'); } catch {}
    if (me && c && c.at > at && c.sub === w.sub && c.server === w.server && c.room !== w.room) {
      const want = c.s && STATES[c.s.s] && (!c.s.until || c.s.until > Date.now() + 5000) ? c.s : null;
      o.send(want ? { t: 'status', ...want, carry: c.at } : { t: 'status', s: 'available', carry: c.at });
      return; // the office answers with its own status_at (and my status, if it took it)
    }
    remember();
  }

  const WHY = { rate: 'status.err.rate', past: 'status.err.past', invalid: 'status.err.invalid' }; // {t:"status_err"} reason -> text
  return {
    refresh() { chosenAt = null; refresh(); reconcile(); }, // welcome: a new connection (its status_at follows)
    /** Someone touched the 小窗 (public/pip.js): not idle. */
    poke,
    /** {t:"status_at"} (only to me) */
    at(m) { if (typeof m.at === 'number') arrived(m.at); }, // welcome: my status from the floor I came from; a new connection knows nothing of my automatic 离开
    /** {t:"status_err"}: the office refused my change (it sends my status as it holds right after) */
    err(m) { o.toast(m.reason === 'game' && typeof m.code === 'string' && has(`chat.game.${m.code}`) ? t(`chat.game.${m.code}`) : m.reason === 'game' && typeof m.text === 'string' ? m.text.slice(0, 40) : t(WHY[m.reason] ?? WHY.invalid)); },
    msg(m) {
      const me = o.me(), p = m.id === me?.id ? me : o.peers().get(m.id);
      if (!p) return;
      if (m.status) p.status = m.status; else delete p.status;
      if (p === me) { refresh(); remember(); reconcile(); } // reconcile: back while my 离开 was on its way // back while my 离开 was on its way
      o.changed?.();
    },
  };
}
